import { useEffect, useRef, useState } from "react";
import { Sparkle, X, PaperPlaneRight, MapTrifold } from "@phosphor-icons/react";
import api, { API_BASE } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const SID_KEY = "mise_assistant_session";
const QUICK = [["daily", "Daily report"], ["weekly", "Weekly report"], ["monthly", "Monthly report"]];

/** Floating MISE Intelligence bubble — available on every authenticated screen. */
export function AssistantBubble() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sidRef = useRef(localStorage.getItem(SID_KEY) || "");
  const bottomRef = useRef(null);
  const isMgr = user && ["owner", "manager"].includes(user.role);

  useEffect(() => {
    if (sidRef.current && messages.length === 0) {
      api.get(`/assistant/history?session_id=${sidRef.current}`)
        .then((r) => setMessages(r.data.map((m) => ({ role: m.role, content: m.content })))).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setOpen(true);
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "", tool: null }]);
    try {
      const res = await fetch(`${API_BASE}/assistant/chat`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json",
                   "X-Venue-Id": localStorage.getItem("mise_active_venue") || "" },
        body: JSON.stringify({ message: msg, session_id: sidRef.current || undefined }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.t) {
            setMessages((m) => m.map((x, i) => (i === m.length - 1 ? { ...x, content: x.content + ev.t } : x)));
          }
          if (ev.tool) {
            setMessages((m) => m.map((x, i) => (i === m.length - 1 ? { ...x, tool: ev.tool } : x)));
          }
          if (ev.error) throw new Error(ev.error);
          if (ev.done && ev.session_id) {
            sidRef.current = ev.session_id;
            localStorage.setItem(SID_KEY, ev.session_id);
          }
        }
      }
    } catch (e) {
      setMessages((m) => m.map((x, i) => (i === m.length - 1
        ? { ...x, content: x.content || "Sorry — I couldn't answer that just now. Try again." } : x)));
    } finally {
      setBusy(false);
    }
  };

  const newChat = () => {
    sidRef.current = "";
    localStorage.removeItem(SID_KEY);
    setMessages([]);
  };

  return (
    <>
      {open && (
        <div className="fixed bottom-20 right-4 z-[80] w-[min(380px,calc(100vw-2rem))] rounded-xl border border-border shadow-2xl flex flex-col overflow-hidden"
          style={{ background: "#151226", maxHeight: "min(70vh, 560px)" }} data-testid="assistant-bubble-panel">
          <div className="flex items-center justify-between px-3 py-2 border-b border-border">
            <p className="text-sm font-heading font-semibold flex items-center gap-1.5">
              <Sparkle size={15} className="text-primary" /> MISE Intelligence
            </p>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" data-testid="assistant-bubble-new" onClick={newChat}>New</Button>
              )}
              <button className="p-1 text-muted-foreground hover:text-white" data-testid="assistant-bubble-close" onClick={() => setOpen(false)}>
                <X size={16} />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-[120px]" data-testid="assistant-bubble-messages">
            {messages.length === 0 && (
              <div className="text-xs text-muted-foreground space-y-2">
                <p>Ask me how MISE works, or about your numbers — margins, price moves, cost trends.</p>
                <div className="flex flex-wrap gap-1.5">
                  {isMgr && QUICK.map(([p, label]) => (
                    <button key={p} data-testid={`assistant-quick-${p}`} onClick={() => send(`Generate the ${p} report for my venue.`)}
                      className="px-2.5 py-1 rounded-full border border-primary/40 text-primary text-[11px] hover:bg-primary/10 transition-colors">
                      {label}
                    </button>
                  ))}
                  {isMgr && (
                    <button data-testid="assistant-quick-tour" onClick={() => window.dispatchEvent(new Event("mise:start-tour"))}
                      className="px-2.5 py-1 rounded-full border border-border text-muted-foreground text-[11px] hover:bg-white/5 transition-colors flex items-center gap-1">
                      <MapTrifold size={12} /> Setup tour
                    </button>
                  )}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} data-testid={`assistant-bubble-msg-${i}`}
                className={`text-sm rounded-lg px-3 py-2 whitespace-pre-wrap ${m.role === "user" ? "bg-primary/10 ml-8" : "bg-accent/60 mr-8"}`}>
                {m.content || (busy && i === messages.length - 1 ? "…" : "")}
                {m.tool && (
                  <p className="text-[10px] text-primary/70 mt-1" data-testid={`assistant-bubble-tool-${i}`}>answered from live venue data</p>
                )}
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          <div className="flex gap-2 p-2 border-t border-border">
            <Input data-testid="assistant-bubble-input" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }}
              placeholder="Ask MISE…" disabled={busy} className="h-9 text-sm" />
            <Button data-testid="assistant-bubble-send" onClick={() => send()} disabled={busy || !input.trim()}
              className="h-9 bg-primary hover:bg-[#8B5CF6] text-white px-3">
              <PaperPlaneRight size={15} />
            </Button>
          </div>
        </div>
      )}
      <button
        data-testid="assistant-bubble"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-4 right-4 z-[80] w-14 h-14 rounded-full bg-primary hover:bg-[#8B5CF6] text-white shadow-lg shadow-primary/30 flex items-center justify-center transition-transform hover:scale-105"
        title="MISE Intelligence">
        <Sparkle size={24} weight="fill" />
      </button>
    </>
  );
}
